/* eslint-disable max-len */
import { Box, Grid, Typography } from '@mui/material';
import ButtonForm from 'components/buttons/button-form';
import { validationSchemaComunidades } from 'components/forms/adicionar_comunidade/form_comunidades.validate';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import InputComLabelFormik from 'components/shared/custom_input_label_formik';
import InputSelectFormik from 'components/shared/custom_input_select_formik';
import { FormikProps, useFormik } from 'formik';
import { FormularioComunidades, FormularioComunidadesFormik } from 'interfaces/formularioComunidades.interface';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import useComunidadesRuraisTradicionaisStore, { IComunidadesRuraisTradicionais } from 'stores/forms/comunidades_rurais_tradicionais';
import { useStep1Helpers } from '../step1.hook';
import { mapAssentamentoToForm, mapComunidadeToForm, generateUniqueId } from '../step1.helpers';

interface Step1Props {
  onNext: () => void;
  formik: FormikProps<FormularioComunidadesFormik>;
  open: boolean;
  setOpen: (open: boolean) => void;
  onBack?: () => void;
  stepAtual: number;
  totalSteps: number;
}

type WizardState = {
  mode?: 'edit' | 'create';
  addType?: 'assentamentos' | 'comunidades';
  index?: number;
  item?: any;
  backTo?: string;
  pacoteId?: string;
};

// helpers moved to step1.helpers and useStep1 hook in step1.hook

/*
 * Exposed factory to make the advanced handler pure/testable.
 * Usage: const handleAdvanced = createHandleAdvanced(setComunidades..., saveStep1, onNext, comunidades, navState)
 * then call `await handleAdvanced(localFormik)`
 */
export const createHandleAdvanced = (
  setComunidadesRuraisTradicionais: (v: any) => void,
  saveStep1Fn: () => Promise<boolean>,
  onNextFn: () => void,
  comunidadesRuraisTradicionaisArg: IComunidadesRuraisTradicionais | any,
  navStateArg: WizardState | any
) => {
  return async (localFormikArg: any) => {
    const errors = await localFormikArg.validateForm();
    localFormikArg.setTouched(
      Object.keys(localFormikArg.values).reduce((acc: any, k: any) => ({ ...acc, [k]: true }), {}),
      true
    );
    if (Object.keys(errors).length > 0) return;

    const v: Partial<FormularioComunidades> = localFormikArg.values;

    const codigoPaNum =
      typeof v.codigo_do_pa === 'string'
        ? Number(v.codigo_do_pa.replace(/\D/g, '')) || null
        : (v.codigo_do_pa as any);

    const tipoCadastroNum =
      typeof v.tipo_cadastro === 'number' ? v.tipo_cadastro : Number(v.tipo_cadastro) || null;

    let tipoDeAssentamento: number | null = null;

    if (tipoCadastroNum === 1) {
      if (typeof v.tipo_de_assentamento === 'number') {
        tipoDeAssentamento = v.tipo_de_assentamento;
      } else {
        tipoDeAssentamento = Number(v.tipo_de_assentamento) || null;
      }
    }

    const partial: Partial<IComunidadesRuraisTradicionais> = {
      ano_referencia_do_questionario:
        typeof v.ano_referencia === 'number' ? v.ano_referencia : Number(v.ano_referencia) || null,
      tipo_cadastro: tipoCadastroNum,
      tipo_de_assentamento: tipoDeAssentamento,
      codigo_do_pa: tipoCadastroNum === 1 ? codigoPaNum : null,
      nome_do_assentamento: v.nome_do_assentamento ?? null,
      quantidade_de_familias:
        typeof v.quantidade_de_familias === 'number'
          ? v.quantidade_de_familias
          : Number(v.quantidade_de_familias) || null,
    };

    setComunidadesRuraisTradicionais({
      ...comunidadesRuraisTradicionaisArg,
      ...partial,
      current_form_unique_id: (localFormikArg.values as any).unique_id,
      current_add_type: navStateArg.addType ?? (tipoCadastroNum === 1 ? 'assentamentos' : 'comunidades'),
      current_index: navStateArg.index ?? null,
    } as IComunidadesRuraisTradicionais);

    const ok = await saveStep1Fn();
    if (!ok) {
      // leave behavior identical to existing component
      // eslint-disable-next-line no-alert
      alert('Erro ao salvar este passo');
      return;
    }

    onNextFn();
  };
};

export const Step1: React.FC<Step1Props> = ({
  onNext,
  onBack,
  stepAtual,
  totalSteps
}) => {
  const location = useLocation();
  const navState = (location.state ?? {}) as WizardState;

  const {
    comunidadesRuraisTradicionais,
    setComunidadesRuraisTradicionais,
    saveStep1,
  } = useComunidadesRuraisTradicionaisStore();

  const [isSubmitting] = useState(true);

  const {
    initialValues,
    tiposAssentamento,
    tiposCadastro,
  } = useStep1Helpers(navState, comunidadesRuraisTradicionais as any);

  // Removido o useEffect que zerava os campos em modo 'create'
  // Isso causava perda de dados ao navegar entre os passos
  // A inicialização dos valores já é tratada pelo hook useStep1Helpers

  // initialValues provided by useStep1Helpers

  const localFormik = useFormik<Partial<FormularioComunidades>>({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationSchemaComunidades,
    validateOnChange: !isSubmitting,
    validateOnBlur: !isSubmitting,
    onSubmit: () => { },
  });

  const isAssentamento = localFormik.values.tipo_cadastro === 1;

  // option lists provided by useStep1Helpers

  const handleAdvanced = createHandleAdvanced(
    setComunidadesRuraisTradicionais,
    saveStep1,
    onNext,
    comunidadesRuraisTradicionais,
    navState
  );



  return (
    <>
      <Typography
        variant="h5"
        sx={{ mt: 4, mb: 3, fontSize: '24px', fontWeight: 400, textAlign: 'center' }}
      >
        Informações Básicas
      </Typography>

      <Box>
        <ContainerFormFormik
          title="Informações Básicas"
          subtitle={`Ano de referência do Questionário: ${new Date().getFullYear() - 1}`}
          onSubmit={onNext}
        >
          <Grid item xs={12} mb={1}>
            <InputSelectFormik
              formik={localFormik}
              label="tipo_cadastro"
              labelTexto="Tipo de Comunidade"
              required
              margin="dense"
              options={tiposCadastro}
            />
          </Grid>

          {isAssentamento && (
            <Box>
              <Grid item xs={12} mb={2}>
                <InputSelectFormik
                  formik={localFormik}
                  label="tipo_de_assentamento"
                  labelTexto="Tipo de Assentamento"
                  required
                  margin="dense"
                  options={tiposAssentamento}
                />
              </Grid>

              <Grid item xs={12} mb={3}>
                <InputComLabelFormik
                  label="codigo_do_pa"
                  formik={localFormik}
                  labelTexto="Código"
                  placeholder="Digite o código"
                  version="primary"
                  mask="custom"
                  customMask={(value: string) => value.slice(0, 9)}
                  maxLength={9}
                  required
                />
              </Grid>
            </Box>
          )}

          <Grid item xs={12} mb={3}>
            <InputComLabelFormik
              label="quantidade_de_familias"
              formik={localFormik}
              labelTexto="Quantidade de Famílias"
              version="primary"
              type="number"
              mask="numero"
              required
              acceptFraction={false}
            />
          </Grid>

          <Grid item xs={12} mb={3}>
            <InputComLabelFormik
              label="nome_do_assentamento"
              formik={localFormik}
              labelTexto="Nome da Comunidade"
              placeholder="Nome da Comunidade"
              version="primary"
              required
            />
          </Grid>
        </ContainerFormFormik>

        <ButtonForm
          disabled={false}
          onBack={onBack}
          onAdvanced={() => handleAdvanced(localFormik)}
          onSendApproved={() => handleAdvanced(localFormik)}
          stepAtual={stepAtual}
          totalSteps={totalSteps}
        />
      </Box>
    </>
  );
};
